import { useAuthenticator } from '@aws-amplify/ui-react'
import { createContext, ReactNode, useContext } from 'react'

// This is the React half of the auth seam that lets demo mode run the app
// without Cognito. Auth is abstracted in two places, split by how it's
// consumed:
//
//   - Reactive state components render from (am I signed in? how do I sign
//     out?) — this context.
//   - Imperative async calls made from effects and event handlers
//     (getUserAttributes, getUserPermissions) — see functions.ts.
//
// Production wires RealAuthProvider below (index.tsx, inside Amplify's
// Authenticator.Provider); demo mode wires DemoAuthProvider instead
// (src/demo/auth/DemoAuthProvider.tsx), backed by an in-memory store.
// Components must not import useAuthenticator directly — that would bypass
// the seam and crash in demo mode, where Amplify is never configured.

export type AuthState = {
    // Amplify authenticator route, e.g. 'authenticated' | 'signIn' | 'setup'.
    // App.tsx switches on it to show either the app or the login screen.
    route: string
    signOut: () => void
}

export const AuthContext = createContext<AuthState | null>(null)

/**
 * Auth facade. Components consume this instead of Amplify's useAuthenticator
 * so demo mode can supply a fake implementation (see src/demo/auth/).
 */
export function useAuth(): AuthState {
    const state = useContext(AuthContext)
    if (!state) {
        throw new Error('useAuth must be used inside RealAuthProvider or DemoAuthProvider')
    }
    return state
}

/** Production implementation: delegates to Amplify. Must render inside Authenticator.Provider. */
export function RealAuthProvider({ children }: { children: ReactNode }) {
    const { route, signOut } = useAuthenticator((context) => [context.route])
    return <AuthContext.Provider value={{ route, signOut }}>{children}</AuthContext.Provider>
}
